import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/common/utils";

const buttonVariants = cva(
  "focus-visible:ring-ring inline-flex items-center justify-center gap-2 text-base font-[var(--font-weight-button)] whitespace-nowrap transition-all duration-100 select-none focus-visible:ring-1 focus-visible:outline-none active:scale-95 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-background border border-[var(--button-border)] text-[var(--button-text)] shadow-sm hover:bg-[var(--button-hover-bg)]",
        primary:
          "text-primary-foreground bg-[var(--button-primary-bg)] shadow hover:bg-[var(--button-primary-bg-hover)]",
        "primary-completed":
          "text-primary-foreground bg-[var(--button-completed)] shadow hover:bg-[var(--button-completed-hover)]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        outline:
          "bg-background border border-[var(--button-outline-border)] hover:bg-[var(--button-outline-hover)]",
        secondary:
          "border-border border bg-[var(--button-secondary-bg)] text-[var(--button-text)] shadow-sm hover:bg-[var(--button-hover-bg)]",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        wondercat:
          "bg-gradient-to-br from-[#b308a7] to-[#8b06a0] text-white shadow hover:opacity-90",
        steward:
          "rounded-none bg-[var(--ink)] text-[11px] font-[var(--mono)] tracking-[0.14em] text-[var(--ground)] uppercase hover:-translate-y-px hover:bg-[var(--ink)]",
        "steward-ghost":
          "rounded-none border border-[var(--hair-strong)] bg-transparent text-[11px] font-[var(--mono)] tracking-[0.14em] text-[var(--steward-muted)] uppercase hover:border-[var(--ink)] hover:text-[var(--ink)]",
      },
      size: {
        default: "h-9 rounded-[var(--radius-button)] px-4",
        lg: "h-12 rounded-xl px-6 text-base",
        md: "h-10 rounded-xl px-5 text-sm",
        sm: "h-9 rounded-lg px-4 text-sm",
        xs: "h-8 rounded-lg px-3 text-xs",
        icon: "h-9 w-9 rounded-[var(--radius-button)]",
      },
      rounded: {
        default: "",
        full: "rounded-full",
      },
    },
    compoundVariants: [
      // Steward CTAs are square by design. Without this the size variant's
      // rounded-lg/xl wins the tailwind-merge over the variant's rounded-none,
      // leaving Steward buttons rounded. compoundVariants append last, so they win.
      { variant: ["steward", "steward-ghost"], class: "rounded-none" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.memo(function Button({
  className,
  rounded,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ rounded, variant, size, className }))}
      {...props}
    />
  );
});

export { Button, buttonVariants };
